import { ErrorBoundary } from 'react-error-boundary';
import { Route, Routes } from 'react-router-dom';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import GlobalLayout from './components/GlobalLayout/GlobalLayout';
import { ROUTES } from './constants/constants';
import Accounts, { accountsRef } from './pages/accounts/Accounts';
import SinglePlanPage from './pages/accounts/components/plans/SinglePlanPage';
import SingleAccount from './pages/accounts/components/SingleAccount';
import { CdkRedirect } from './pages/CDK/CdkRedirect';
import Companies from './pages/companies/Companies';
import SingleCompany from './pages/companies/SingleCompany';
import Dashboard from './pages/dashboard/Dashboard';
import HomePage from './pages/homePage/HomePage';
import NotFound from './pages/notFound/NotFound';
import { PrivateRoute } from './pages/private/PrivatePage';
import Settings, { settingsRef } from './pages/settings/Settings';
import PlansList from './pages/accounts/components/plans/PlansList';
import useUpdatePageMeta from './hooks/useUpdatePageMeta';
import Gleap from 'gleap';
import { useGetUser } from './hooks/useGetUser';
import { useEffect } from 'react';
import Residuals from './pages/residuals/Residuals';
import ResidualsView from './pages/residuals/ResidualsView';
import * as Sentry from '@sentry/react';
import { redactUrl } from './utils/sentry';

function App() {
  const { currentUser, company } = useGetUser();
  useUpdatePageMeta();

  useEffect(() => {
    if (currentUser && company) {
      Gleap.identify(currentUser._id, {
        email: currentUser.email,
        name: currentUser.firstName + ' ' + currentUser.lastName,
        phone: currentUser.phone,
        companyName: company?.name,
        companyId: company?._id,
      });
    }
  }, [currentUser, company]);
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        // Capture error in Sentry with additional context
        Sentry.captureException(error, {
          tags: {
            component: 'ErrorBoundary',
            errorBoundary: true,
          },
          extra: {
            errorBoundary: true,
            componentStack: errorInfo.componentStack,
            url: redactUrl(window.location.href),
          },
          level: 'error',
        });
      }}
    >
      <Routes>
        <Route element={<PrivateRoute />}>
          <Route path='/' element={<HomePage />} />
          <Route path={`${ROUTES.CDK}/*`} element={<GlobalLayout />}>
            <Route index element={<CdkRedirect />} />
            <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
            <Route path={`${ROUTES.DASHBOARD}/:id`} element={<Dashboard />} />
            <Route path={ROUTES.SETTINGS} element={<Settings />} />
            <Route
              path={`${ROUTES.SETTINGS}/:id/:info`}
              element={<SingleAccount baseUrl={settingsRef} backUrl='/cdk' />}
            />
            <Route path={`${ROUTES.PLANS}`} element={<PlansList />} />
            <Route path={`${ROUTES.PLANS}/:id`} element={<SinglePlanPage baseUrl='/cdk/plans' />} />
            <Route path={ROUTES.ORGANIZATIONS} element={<Accounts />} />
            <Route
              path={`${ROUTES.ORGANIZATIONS}/:id`}
              element={<SingleAccount baseUrl={accountsRef} />}
            />
            <Route
              path={`${ROUTES.ORGANIZATIONS}/:id/:info/plan`}
              element={<SinglePlanPage baseUrl={`/cdk/${ROUTES.ORGANIZATIONS}`} />}
            />
            <Route
              path={`${ROUTES.ORGANIZATIONS}/:id/:info`}
              element={<SingleAccount baseUrl={accountsRef} />}
            ></Route>
            <Route path={ROUTES.COMPANIES} element={<Companies />} />
            <Route path={`${ROUTES.COMPANIES}/:id`} element={<SingleCompany />} />
            <Route path={`${ROUTES.COMPANIES}/:id/:info`} element={<SingleCompany />} />
            <Route path={ROUTES.RESIDUALS} element={<Residuals />} />
            <Route path={`${ROUTES.RESIDUALS}/:reportId`} element={<ResidualsView />} />
          </Route>
        </Route>
        <Route path='*' element={<NotFound />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
